/**
 * Расход — сколько ассистент, голосовой помощник и WhatsApp обходятся
 * платформе за месяц:
 * итог по всем клиникам и таблица по клиникам (дороже — выше). Основа для
 * цен и тарифных пакетов. Данные — GET admin/usage?period=YYYY-MM.
 */

import {type ReactNode, useState} from 'react';
import {useQuery} from '@tanstack/react-query';
import dayjs from 'dayjs';
import {ChevronLeft, ChevronRight} from 'lucide-react';

import {paidMessages, platformUsageQueryOptions} from '@services/usage';
import {Loader} from '@components/ui/Loader';
import {UsageTotalsGrid} from '@components/UsageTotalsGrid';
import {formatCompact, formatNumber, formatTiyin} from '@utils/format';

export const Usage = () => {
  /** 0 — текущий месяц, 1 — прошлый… */
  const [back, setBack] = useState(0);
  const month = dayjs().subtract(back, 'month');
  const period = month.format('YYYY-MM');
  const {data, isLoading} = useQuery(platformUsageQueryOptions(period));
  const clinics = [...(data?.clinics ?? [])].sort(
    (a, b) => b.estimatedCostTiyin - a.estimatedCostTiyin || b.totals.dialogs - a.totals.dialogs,
  );

  return (
    <>
      <header className="sticky top-0 z-10 flex flex-wrap items-center gap-4 border-b border-skr-line bg-background/80 px-[30px] py-[18px] backdrop-blur max-md:px-4">
        <div className="min-w-0 flex-1">
          <h1 className="text-[21px] font-bold -tracking-[.02em]">Расход</h1>
          <div className="mt-0.5 text-[13px] text-muted-foreground">
            Ассистент, голос и WhatsApp по клиникам · себестоимость по ставкам
          </div>
        </div>
        <div className="flex items-center gap-1 rounded-[10px] border border-skr-line bg-card p-[3px]">
          <MonthButton label="Предыдущий месяц" onClick={() => setBack((b) => b + 1)}>
            <ChevronLeft className="size-4" />
          </MonthButton>
          <span className="min-w-[120px] text-center text-[13px] font-semibold capitalize">
            {month.format('MMMM YYYY')}
          </span>
          <MonthButton
            label="Следующий месяц"
            disabled={back === 0}
            onClick={() => setBack((b) => b - 1)}
          >
            <ChevronRight className="size-4" />
          </MonthButton>
        </div>
      </header>

      <div className="flex max-w-[1080px] flex-col gap-4 px-[30px] pb-[60px] pt-[22px] max-md:px-4">
        {isLoading || !data ? (
          <div className="flex min-h-[360px] items-center justify-center">
            <Loader label="Считаем расход…" />
          </div>
        ) : (
          <>
            <UsageTotalsGrid
              totals={data.totals}
              costTiyin={data.estimatedCostTiyin}
              rateCard={data.rateCard}
            />
            <div className="overflow-x-auto rounded-[13px] border border-skr-line bg-card">
              <table className="w-full min-w-[700px] text-[13px]">
                <thead>
                  <tr className="border-b border-skr-line text-left text-[12px] text-muted-foreground">
                    <th className="px-4 py-2.5 font-medium">Клиника</th>
                    <th className="px-3 py-2.5 text-right font-medium">Диалоги</th>
                    <th className="px-3 py-2.5 text-right font-medium">Токены</th>
                    <th className="px-3 py-2.5 text-right font-medium">Голос</th>
                    <th className="px-3 py-2.5 text-right font-medium">Платные WA</th>
                    <th className="px-3 py-2.5 text-right font-medium">Бесплатные</th>
                    <th className="px-4 py-2.5 text-right font-medium">Смета</th>
                  </tr>
                </thead>
                <tbody className="font-mono">
                  {clinics.length === 0 ? (
                    <tr>
                      <td
                        colSpan={7}
                        className="px-4 py-8 text-center font-sans text-muted-foreground"
                      >
                        За месяц расхода не было
                      </td>
                    </tr>
                  ) : (
                    clinics.map((c) => (
                      <tr key={c.clinicId} className="border-b border-skr-hairline last:border-0">
                        <td className="px-4 py-2.5 font-sans font-semibold">{c.clinicName}</td>
                        <td className="px-3 py-2.5 text-right">{formatNumber(c.totals.dialogs)}</td>
                        <td className="px-3 py-2.5 text-right">
                          {formatCompact(c.totals.inputTokens + c.totals.outputTokens)}
                        </td>
                        <td className="px-3 py-2.5 text-right">
                          {formatNumber(c.totals.voiceCalls)}
                        </td>
                        <td className="px-3 py-2.5 text-right">
                          {formatNumber(paidMessages(c.totals))}
                        </td>
                        <td className="px-3 py-2.5 text-right">
                          {formatNumber(c.totals.waServiceFree)}
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          {c.estimatedCostTiyin > 0 ? formatTiyin(c.estimatedCostTiyin) : '—'}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </>
  );
};

const MonthButton = ({
  label,
  disabled = false,
  onClick,
  children,
}: {
  label: string;
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}) => (
  <button
    aria-label={label}
    disabled={disabled}
    onClick={onClick}
    className="grid size-8 place-items-center rounded-[8px] text-muted-foreground transition hover:bg-skr-head disabled:opacity-30"
  >
    {children}
  </button>
);
